import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Plus, Trash2, Edit3 } from "lucide-react";

const PERMISSIONS = [
  { code: "entreprises.read", label: "Lire les entreprises" },
  { code: "entreprises.write", label: "Modifier les entreprises" },
  { code: "salaries.read", label: "Lire les salariés" },
  { code: "salaries.write", label: "Modifier les salariés" },
  { code: "conventions.read", label: "Lire les conventions" },
  { code: "conventions.write", label: "Rédiger les conventions" },
  { code: "conventions.sign", label: "Signer les conventions" },
  { code: "timesheets.validate", label: "Valider les feuilles de temps" },
  { code: "appels.create", label: "Créer des appels à contribution" },
  { code: "appels.pay", label: "Enregistrer les paiements" },
  { code: "registre.read", label: "Consulter le registre des membres" },
  { code: "registre.manage", label: "Gérer le registre des membres" },
  { code: "ged.read", label: "Consulter la GED" },
  { code: "ged.write", label: "Déposer dans la GED" },
  { code: "ai.use", label: "Utiliser ZOE (assistante IA)" },
  { code: "tasks.assign", label: "Assigner des tâches" },
];

const INHERIT_OPTIONS = ["", "gestionnaire_ge", "finance", "employeur", "medical", "auditeur"];

export default function Roles() {
  const [roles, setRoles] = useState({ builtin: [], custom: [] });
  const [editing, setEditing] = useState(null); // null | {} | role
  const [form, setForm] = useState({ code: "", label: "", description: "", permissions: [], inherits_from: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur chargement");
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ code: "", label: "", description: "", permissions: [], inherits_from: "" });
    setEditing({});
  };
  const openEdit = (r) => {
    setForm({ code: r.code, label: r.label, description: r.description || "", permissions: r.permissions || [], inherits_from: r.inherits_from || "" });
    setEditing(r);
  };

  const togglePerm = (code) => {
    setForm(f => ({ ...f, permissions: f.permissions.includes(code) ? f.permissions.filter(p => p !== code) : [...f.permissions, code] }));
  };

  const save = async () => {
    if (!form.code || !form.label) { toast.error("Code et libellé requis"); return; }
    try {
      const payload = { ...form, inherits_from: form.inherits_from || null };
      if (editing?.id) {
        await api.put(`/roles/${editing.id}`, payload);
        toast.success("Rôle mis à jour");
      } else {
        await api.post("/roles", payload);
        toast.success("Rôle créé");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur enregistrement");
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer le rôle "${r.label}" ?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Rôle supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="roles-page">
      <PageHeader
        title="Rôles & permissions"
        subtitle="Rôles système et rôles personnalisés que le super-admin peut composer."
        icon={Shield}
        actions={<Button onClick={openNew} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="new-role-btn"><Plus className="h-4 w-4 mr-2" />Nouveau rôle</Button>}
      />

      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Rôles système</h3>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          {roles.builtin?.map(r => (
            <div key={r.code} className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="font-semibold">{r.label}</div>
              <div className="text-xs text-slate-500 mt-1 font-mono">{r.code}</div>
              <Badge className="mt-2" variant="outline">Système</Badge>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Rôles personnalisés</h3>
        {roles.custom?.length === 0 ? (
          <p className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-6 text-center">
            Aucun rôle personnalisé. Cliquez « Nouveau rôle » pour en créer.
          </p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {roles.custom?.map(r => (
              <div key={r.id} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`custom-role-${r.code}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold">{r.label}</div>
                    <div className="text-xs text-slate-500 font-mono">{r.code}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><Edit3 className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(r)} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                {r.description && <p className="text-xs text-slate-600 mt-2">{r.description}</p>}
                {r.inherits_from && <div className="mt-2 text-xs"><Badge variant="outline">Hérite de {r.inherits_from}</Badge></div>}
                <div className="mt-2 flex flex-wrap gap-1">
                  {(r.permissions || []).slice(0, 6).map(p => <Badge key={p} variant="outline" className="text-[10px]">{p}</Badge>)}
                  {r.permissions?.length > 6 && <Badge variant="outline" className="text-[10px]">+{r.permissions.length - 6}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier le rôle" : "Nouveau rôle personnalisé"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Code (slug)</label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, "_") })} disabled={!!editing?.id} data-testid="role-code" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Libellé</label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="role-label" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Hérite d'un rôle système</label>
              <Select value={form.inherits_from} onValueChange={(v) => setForm({ ...form, inherits_from: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {INHERIT_OPTIONS.filter(Boolean).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Permissions</label>
              <div className="grid grid-cols-2 gap-2 mt-1 max-h-56 overflow-y-auto border border-slate-200 rounded-md p-2">
                {PERMISSIONS.map(p => (
                  <label key={p.code} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.permissions.includes(p.code)} onCheckedChange={() => togglePerm(p.code)} />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} className="bg-[#C5A059] hover:bg-[#A88846]" data-testid="save-role-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
